import { useEffect, useRef, useState } from "react";
import { FaMapMarkerAlt } from "react-icons/fa";

const formatFieldValue = (value) => {
	if (value === null || value === undefined || value === "") {
		return "No value";
	}
	if (typeof value === "object") {
		return JSON.stringify(value);
	}
	return String(value);
};

const AerialFieldDetail = ({
	field,
	marker,
	markerOutsideImage,
	placementActive,
	readOnly,
	saving,
	onBeginPlacement,
	onCancelPlacement,
	onPreviewMarker,
	onSaveMarker,
	onDeleteMarker,
	onViewProjectField,
}) => {
	const initialMarkerNote = marker?.note || "";
	const [note, setNote] = useState(initialMarkerNote);
	const [noteSaveState, setNoteSaveState] = useState("idle");
	const [deleting, setDeleting] = useState(false);
	const saveTimerReference = useRef(null);
	const lastSavedNoteReference = useRef(initialMarkerNote);

	useEffect(
		() => () => {
			if (saveTimerReference.current) {
				clearTimeout(saveTimerReference.current);
			}
		},
		[],
	);

	if (!field) {
		return <p className="p-4 text-sm text-gray-500">Select a field from the catalogue or select a marker on the image.</p>;
	}

	const saveNote = async (nextNote) => {
		if (!marker || nextNote === lastSavedNoteReference.current) {
			return;
		}
		setNoteSaveState("saving");
		try {
			await onSaveMarker({ ...marker, note: nextNote });
			lastSavedNoteReference.current = nextNote;
			setNoteSaveState("saved");
		} catch (error) {
			setNoteSaveState("error");
		}
	};

	const updateNote = (changeEvent) => {
		const nextNote = changeEvent.target.value;
		setNote(nextNote);
		onPreviewMarker({ ...marker, note: nextNote });
		if (saveTimerReference.current) {
			clearTimeout(saveTimerReference.current);
		}
		saveTimerReference.current = setTimeout(() => saveNote(nextNote), 700);
	};

	const flushNote = () => {
		if (saveTimerReference.current) {
			clearTimeout(saveTimerReference.current);
		}
		saveNote(note);
	};

	const deleteMarker = async () => {
		if (!marker || readOnly || deleting) {
			return;
		}
		if (saveTimerReference.current) {
			clearTimeout(saveTimerReference.current);
			saveTimerReference.current = null;
		}
		setDeleting(true);
		try {
			await onDeleteMarker(marker);
		} finally {
			setDeleting(false);
		}
	};

	return (
		<div className="space-y-4 p-4">
			<div>
				<p className="text-xs font-semibold uppercase text-gray-500">Field</p>
				<h3 className="text-lg font-bold">{field.name}</h3>
				{field.description ? <p className="mt-1 text-sm text-gray-600">{field.description}</p> : null}
			</div>
			<div>
				<p className="text-xs font-semibold uppercase text-gray-500">Value</p>
				<p className="whitespace-pre-wrap break-words rounded bg-gray-100 p-2 text-sm">{formatFieldValue(field.value)}</p>
			</div>
			<div>
				<p className="text-xs font-semibold uppercase text-gray-500">Field comment</p>
				<p className="whitespace-pre-wrap break-words rounded bg-gray-100 p-2 text-sm">{field.comment || "No comment"}</p>
			</div>

			{marker ? (
				<>
					<div className="rounded border border-gray-200 p-3">
						<p className="flex items-center gap-2 font-semibold">
							<FaMapMarkerAlt className="text-red-600" />
							Marker placed
						</p>
						<p className="mt-1 text-xs text-gray-500">
							X {(marker.relativeXCord * 100).toFixed(2)}% · Y {(marker.relativeYCord * 100).toFixed(2)}%
						</p>
						{Number(marker.lat) !== 0 || Number(marker.lon) !== 0 ? (
							<p className="mt-1 text-xs text-gray-500">
								Lat {Number(marker.lat).toFixed(7)} · Lon {Number(marker.lon).toFixed(7)}
							</p>
						) : null}
					</div>
					<div>
						<label className="text-xs font-semibold uppercase text-gray-500" htmlFor={`aerial-marker-note-${field.id}`}>
							Marker note
						</label>
						<textarea
							id={`aerial-marker-note-${field.id}`}
							className="mt-1 min-h-28 w-full rounded border border-gray-300 p-2 text-sm disabled:bg-gray-100"
							value={note}
							maxLength={10000}
							disabled={readOnly}
							onChange={updateNote}
							onBlur={flushNote}
						/>
						<p className={`text-xs ${noteSaveState === "error" ? "text-red-600" : "text-gray-500"}`}>
							{saving || noteSaveState === "saving"
								? "Saving…"
								: noteSaveState === "saved"
									? "Saved"
									: noteSaveState === "error"
										? "Save failed. Change the note or leave the field to retry."
										: ""}
						</p>
					</div>
					<div className="space-y-2">
						<button
							type="button"
							className="w-full rounded bg-blue-600 px-3 py-2 font-semibold text-white hover:bg-blue-700"
							onClick={() => onViewProjectField(field.id)}>
							View field in Project
						</button>
						{readOnly ? null : (
							<>
								{placementActive ? (
									<button type="button" className="w-full rounded bg-gray-600 px-3 py-2 font-semibold text-white" onClick={onCancelPlacement}>
										Cancel marker placement
									</button>
								) : markerOutsideImage ? (
									<button
										type="button"
										className="w-full rounded bg-blue-600 px-3 py-2 font-semibold text-white hover:bg-blue-700"
										onClick={() => onBeginPlacement(field.id)}>
										Relocate marker
									</button>
								) : null}
								<button
									type="button"
									disabled={deleting}
									className="w-full rounded bg-red-600 px-3 py-2 font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-400"
									onClick={deleteMarker}>
									{deleting ? "Deleting…" : "Delete Marker"}
								</button>
							</>
						)}
					</div>
				</>
			) : (
				<div>
					<p className="mb-3 text-sm text-gray-600">This field does not have a marker yet.</p>
					{placementActive ? (
						<button type="button" className="w-full rounded bg-gray-600 px-3 py-2 font-semibold text-white" onClick={onCancelPlacement}>
							Cancel marker placement
						</button>
					) : (
						<button
							type="button"
							disabled={readOnly}
							className="w-full rounded bg-blue-600 px-3 py-2 font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
							onClick={() => onBeginPlacement(field.id)}>
							Add a marker
						</button>
					)}
				</div>
			)}
		</div>
	);
};

export default AerialFieldDetail;
